import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { useSession } from '@nextgen-web-framework/all';
import {
  Filter,
  fulltextSearchSliceReq,
  resetFilter,
  resetSearchResult,
  updateContentSearchText,
  resetContentSearchResult,
  SearchState,
  updateFilter,
  updateLimit,
  updateOffset,
  updateQuickFilter,
  fulltextContentSearchSliceReq,
  updateSearchCurrentTab,
  ContentSearchPayloadType,
} from '@commerce-search-webcomponents/sdk';
import {
  SearchResultProps,
  SearchResultComponentProps,
  SearchResultHocOptionalProps,
} from '../components/SearchResult/types/propTypes';
import { SEARCH_MODE } from '../components/SearchResult/types/enums';
export type SearchResultHocProps = SearchResultProps & SearchResultHocOptionalProps;

const withSearchResultProps = (SearchResultComponent: React.FC<SearchResultComponentProps>) => {
  const HocComponent = (props: SearchResultHocProps) => {
    const state = useSelector((state: { commerceSearch: SearchState }) => state.commerceSearch);
    const dispatch = useDispatch();
    const { session } = useSession();

    const handleUpdateLimit = (limit: number) => dispatch(updateLimit(limit));
    const handleUpdateOffset = (offset: number) => dispatch(updateOffset(offset));
    const handleResetSearchResult = () => dispatch(resetSearchResult());
    const handleUpdateFilter = (filter: Filter) => dispatch(updateFilter(filter));
    const handleUpdateQuickFilter = (filter: string) => dispatch(updateQuickFilter(filter));
    const handleUpdateSearchTab = (tabType: string) => dispatch(updateSearchCurrentTab(tabType));
    const handleResetFilter = () => dispatch(resetFilter());
    const handleResetContentSearchResult = () => dispatch(resetContentSearchResult());
    const handleUpdateContentSearchText = (text: string) => dispatch(updateContentSearchText(text));
    const handleFetchResults = (text: string) => {
      const searchMode = Object.keys(SEARCH_MODE).includes(props.searchMode as string) ? props.searchMode : undefined;
      if (text) {
        dispatch(
          fulltextSearchSliceReq({
            text,
            searchMode,
            warehouseId: session.warehouseId,
          }) as unknown as AnyAction,
        );
      }
    };
    const handleFetchContentResult = (payload: ContentSearchPayloadType) => {
      if (payload.query) {
        dispatch(fulltextContentSearchSliceReq(payload) as unknown as AnyAction);
      }
    };

    const isPLP = props.isPLP ?? props.fulltextSearchResults !== undefined;
    // Note: For SRP, redux will be used and for PLP, props will be used
    return (
      <SearchResultComponent
        {...props}
        isPLP={isPLP}
        offset={props.offset ?? state.offset}
        limit={props.limit || state.limit}
        isLoading={props.isLoading ?? state.isLoading}
        fulltextSearchError={Boolean(isPLP ? props.fulltextSearchError : state.errors?.fulltextSearchError)}
        fulltextSearchResults={props.fulltextSearchResults || state.fulltextSearchResults}
        articleResults={props.articleResults || state.articlesSearchResults}
        offersResults={props.offersResults || state.offersSearchResults}
        contentSearchLoaders={props.contentSearchLoaders || state.contentSearchLoaders}
        facets={props.facets || state.facets}
        filter={props.filter || state.filter}
        availableQuickFilters={props.availableQuickFilters || (!isPLP ? state.availableQuickFilters : [])}
        quickFilter={props.quickFilter ?? (!isPLP ? state.quickFilter : '')}
        originallySearchedText={props.originallySearchedText ?? state.originallySearchedText}
        noResultFilterName={props.noResultFilterName ?? state.noResultFilterName}
        contentSearchText={props.contentSearchText ?? state.contentSearchText}
        updateLimit={props.updateLimit || handleUpdateLimit}
        updateOffset={props.updateOffset || handleUpdateOffset}
        resetSearchResult={props.resetSearchResult || handleResetSearchResult}
        fetchResults={props.fetchResults || handleFetchResults}
        updateFilter={props.updateFilter || handleUpdateFilter}
        updateQuickFilter={props.updateQuickFilter || handleUpdateQuickFilter}
        resetFilter={props.resetFilter || handleResetFilter}
        fetchContentResult={props.fetchContentResult || handleFetchContentResult}
        updateSearchTab={props.updateSearchTab ?? handleUpdateSearchTab}
        resetContentSearchResult={props.resetContentSearchResult || handleResetContentSearchResult}
        updateContentSearchText={props.updateContentSearchText || handleUpdateContentSearchText}
      />
    );
  };
  return HocComponent;
};

export default withSearchResultProps;
